<template>
  <AnalyticsWidget
    title="Glass Sales Summary"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <template v-slot:title>
      <ReportFilterPopover
        class="float-right mr-2"
        headerTitle="Report Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        @apply="applySummaryFilter"
        @reset="resetSummaryFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Range">
            <ReportDateRangeSelect 
                :startDate="editingSummaryFilter.startDate"
                :endDate="editingSummaryFilter.endDate"
                @updateStartDate="editingSummaryFilter.startDate = $event"
                @updateEndDate="editingSummaryFilter.endDate = $event"
              />
          </ReportFilterGroup>

          <ReportFilterGroup label="Contact">
            <ReportContactSelect
              v-model="editingSummaryFilter.contactId"
              @updateName="editingSummaryFilter.contactName = $event"
            />
          </ReportFilterGroup>

          <ReportFilterGroup label="Price Group Filter">
            <FormMultiSelect v-model="editingSummaryFilter.priceGroupList" :options="priceGroupLookup" />
          </ReportFilterGroup>
          
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedSummaryFilter.startDate | formatDate}} - {{appliedSummaryFilter.endDate | formatDate}}
          </div>
          <div v-if="appliedSummaryFilter.priceGroupList">
            <i class="far fa-money-check-edit-alt mr-2"></i>{{appliedSummaryFilter.priceGroupList.length}} Price Group{{displayCountPlural(appliedSummaryFilter.priceGroupList.length)}}
          </div>
          <div v-if="appliedSummaryFilter.stockList && appliedSummaryFilter.stockList.length > 0">
            <i class="far fa-inventory mr-2"></i>{{appliedSummaryFilter.stockList.length}} Stock{{displayCountPlural(appliedSummaryFilter.stockList.length)}}
          </div>
          <div v-if="appliedSummaryFilter.contactId">
            <i class="far fa-user-circle mr-2"></i>{{appliedSummaryFilter.contactName}}
          </div>
        </template>
      </ReportFilterPopover>
    </template>
    <template v-slot:subtitle>
      Displays detailed sales data for stock items sold during a date range<br />
      Based on completed orders and completed order dates. All prices ex-GST
    </template>
    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <AnalyticsBreakdownTable v-if="!loading">
      <template v-slot:header>
        <tr>
          <th> </th>
          <th> </th>
          <th :colspan="displayPriceGroupList.length" class="text-center table-data-alternate-column">Price Group Breakdown</th>
          <th></th>
          <th></th>
        </tr>
        <tr>
          <th>Stock</th>
          <th>Total</th>
          <th class="table-data-alternate-column" v-for="priceGroup of displayPriceGroupList" :key="priceGroup.id">{{priceGroup.name}}</th>
          <th>Services</th>
        </tr>
      </template>
      <template v-slot:body>
        <template v-for="stock of reportData.stocks">
          <tr class="static-datatable-striped" :key="`main-${stock.id}`">
            <td class="font-weight-bold"><LinkStock :id="stock.id" :name="stock.name" /></td>
            <td class="font-weight-bold">{{formatCurrency(stock.totalSales)}}</td>
            <td class="font-weight-bold" v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">{{formatCurrency(priceGroupData.totalSales)}}</td>
            <td class="font-weight-bold">{{formatCurrency(stock.totalServicesSales)}}</td>
          </tr>
          <tr class="table-row-sm" :key="`sqm-${stock.id}`">
            <td><i class="fas fa-caret-right mr-1 ml-3"></i>Square Metres</td>
            <td>{{formatDecimal(stock.totalUnits, 5)}}m&sup2;</td>
            <td v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">{{formatDecimal(priceGroupData.totalUnits, 5)}}m&sup2;</td>
            <td> </td>
          </tr>
          <tr class="table-row-sm" :key="`avg-${stock.id}`">
            <td><i class="fas fa-caret-right mr-1 ml-3"></i>Avg Sale Price / m&sup2;</td>
            <td>{{formatCurrency(stock.avgSalePrice)}}</td>
            <td v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">{{formatCurrency(priceGroupData.avgSalePrice)}}</td>
            <td> </td>
          </tr>
          <tr class="table-row-sm" :key="`cogs-${stock.id}`">
            <td><i class="fas fa-caret-right mr-1 ml-3"></i>Avg Cost of Goods Sold</td>
            <td>
              <Tooltip linkStyle :label="`Moving avg unit price: ${formatCurrency(stock.totalGlassUnitPriceAvg)}`">{{formatCurrency(stock.avgCOGS)}}</Tooltip>
              &nbsp;<span class="small">(<span :class="{'financial-profit': stock.COGSProfitLoss >= 0, 'financial-loss': stock.COGSProfitLoss < 0, 'font-weight-bold': true}">+{{formatCurrency(stock.COGSProfitLoss)}}</span>)</span>
            </td>
            <td v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">
              <Tooltip linkStyle :label="`Moving avg unit price: ${formatCurrency(stock.totalGlassUnitPriceAvg)}`">{{formatCurrency(priceGroupData.avgCOGS)}}</Tooltip>
              &nbsp;<span class="small">(<span :class="{'financial-profit': priceGroupData.COGSProfitLoss >= 0, 'financial-loss': priceGroupData.COGSProfitLoss < 0, 'font-weight-bold': true}">+{{formatCurrency(priceGroupData.COGSProfitLoss)}}</span>)</span>
            </td>
            <td> </td>
          </tr>
          <tr class="table-row-sm" :key="`cog-percent-${stock.id}`">
            <td><i class="fas fa-caret-right mr-1 ml-3"></i>Cost of Goods Sold Percent</td>
            <td>{{formatPercent(stock.COGPercent)}}</td>
            <td v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">{{formatPercent(priceGroupData.COGPercent)}}</td>
            <td> </td>
          </tr>
          <tr class="table-row-sm" :key="`total-tax-${stock.id}`">
            <td><i class="fas fa-caret-right mr-1 ml-3"></i>Total Tax</td>
            <td>{{formatCurrency(stock.totalTax)}}</td>
            <td v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">{{formatCurrency(priceGroupData.totalTax)}}</td>
            <td> </td>
          </tr>
          <tr class="table-row-sm" :key="`gross-total-${stock.id}`">
            <td><i class="fas fa-caret-right mr-1 ml-3"></i>Gross Total</td>
            <td>{{formatCurrency(stock.totalSales + stock.totalTax)}}</td>
            <td v-for="priceGroupData of stock.priceGroupList" :key="priceGroupData.id">{{formatCurrency(priceGroupData.totalSales + priceGroupData.totalTax)}}</td>
            <td> </td>
          </tr>
          <tr class="table-row-sm" :key="`spacer-${stock.id}`">
            <td colspan="9"> </td>
          </tr>
        </template>
      </template>
    </AnalyticsBreakdownTable>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportContactSelect from '@/components/reporting/reportContactSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

export default {
  name: 'InventoryStockGlassSalesBreakdownWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    ReportContactSelect
  },
  props: {
    filter: Object,
    stockId: Number,
    gridWidth: Number,
    gridHeight: Number
  },
  data() {
    return {
      loading: false,
      loadingError: '',

      defaultSummaryFilter: {    
        startDate: moment().local().subtract(30, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        contactId: null,
        contactName: '',
        priceGroupList: []
      },
      editingSummaryFilter: {},
      appliedSummaryFilter: {},

      priceGroupList: [],
      priceGroupLookup: [],
      reportData: {
        stockPriceGroups: [],
        stocks: [],
        totals: {}
      }      
    }
  },
  computed: {
    displayPriceGroupList() {
      return this.priceGroupList.filter(priceGroup => {
        return (this.appliedSummaryFilter.priceGroupList.indexOf(priceGroup.id) != -1)
      })
    }  
  },
  watch: {
    stockId: function() {
      this.requestReportData()
    }    
  },
  mounted() {
    this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
    this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

    this.loading = true
    this.requestData()
  },
  methods: {
    requestData() {
      // Get config data
      this.$apiService.pricingService.getStockPriceGroupList()
      .then(response => {

        this.priceGroupList = response.data.filter(priceGroup => priceGroup.isActive)
        this.priceGroupLookup = this.priceGroupList.map(priceGroup => {
          return {
            value: priceGroup.id,
            name: priceGroup.name
          }
        })
        // Default to all price groups selected
        this.defaultSummaryFilter.priceGroupList = this.priceGroupList.map(priceGroup => priceGroup.id)
        this.resetSummaryFilter()
      })
    },
    requestReportData() {
      var params = {
        startDate: this.appliedSummaryFilter.startDate,
        endDate: this.appliedSummaryFilter.endDate,
        priceGroupIdList: this.appliedSummaryFilter.priceGroupList,
        contactId: this.appliedSummaryFilter.contactId,
        stockIdList: [this.stockId]
      }

      this.loading = true
      this.loadingError = ''
      this.$apiService.analyticsService.getOrdersValueGlassDetailedList(params)
      .then(response => {
        this.reportData = response.data
      })    
      .catch(err => {
        console.error(err)
        this.loadingError = 'Error loading data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    exportDataCSV() {
      var params = {
        startDate: this.appliedSummaryFilter.startDate,
        endDate: this.appliedSummaryFilter.endDate,
        priceGroupIdList: this.appliedSummaryFilter.priceGroupList,
        contactId: this.appliedSummaryFilter.contactId,
        stockIdList: [this.stockId],
        format: 'csv'
      }

      let queryString = buildUrl({ queryParams: params })

      this.$apiService.reportingService.generateReportSignedURL(`/intelligence/orders/ordervalue/detailedlist/glass${queryString}`)
      .then(response => {
        window.location.href = response.data.url
      })
    },
    applySummaryFilter() {
      this.appliedSummaryFilter = _.clone(this.editingSummaryFilter)

      this.requestReportData()
    },
    resetSummaryFilter() {
      this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
      this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

      this.requestReportData()
    }
  }
}
</script>